import Component from '@glimmer/component';
import t from 'ember-intl/helpers/t';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and } from 'ember-truth-helpers';
import InviteSteps from 'invite-sign/components/invite-steps';
import type GroupInviteLink from 'invite-sign/models/group-invite-link';
import type { Step } from 'invite-sign/services/invite-steps';

type GroupInviteSidebarSignature = {
  Args: {
    model: GroupInviteLink;
    steps: Step[];
  };
};

export default class GroupInviteSidebar extends Component<GroupInviteSidebarSignature> {
  get formattedDate(): string | null {
    return this.args.model.formattedExpectedArrivalTime;
  }

  get formattedExpiresAt(): string | null {
    return this.args.model.formattedExpiresAt;
  }

  get formattedRemainingCapacity(): string | null {
    const { model } = this.args;

    if (!model?.remainingCapacity) return null;
    if (model.remainingCapacity > 50) return '50+';

    return `${model.remainingCapacity}`;
  }

  <template>
    {{#if @model}}
      <div class="mt-35 md:absolute md:w-69 md:bottom-0 md:my-4 flex flex-col justify-items-end">
        {{! Alert banner for expiration }}
        {{#if this.formattedExpiresAt}}
          <div
            class="bg-white rounded-lg p-2 m-4 border border-orange-200 text-md text-center relative z-50 md:w-full"
            data-test-expiration-warning
          >
            <p class="text-orange-600 text-xs m-0 font-semibold">
              {{#if @model.isInactiveOrExpired}}
                {{t "group_invite.link_expired"}}
              {{else}}
                {{t "group_invite.expires_on" date=this.formattedExpiresAt}}
              {{/if}}
            </p>
          </div>
        {{/if}}

        {{! Event card - matches Figma design }}
        <div class="shadow bg-white rounded-lg p-6 mx-4 transition relative z-50 md:w-full" data-test-event-details>
          <h3 class="font-semibold text-lg mb-1">
            {{@model.groupName}}
          </h3>
          <p class="text-sm text-gray-700 mb-1">
            {{this.formattedDate}}
          </p>

          {{#unless @model.isInactiveOrExpired}}
            {{! Capacity indicators }}
            {{#if @model.isAtCapacity}}
              <div class="flex items-center text-sm text-orange-600" data-test-capacity-full>
                {{svgJar "capacity" width="16" height="16" class="mr-2 -mt-px"}}
                {{t "group_invite.at_full_capacity"}}
              </div>
            {{else if (and @model.capacityLimit this.formattedRemainingCapacity)}}
              <div class="text-sm">
                <span class="text-grey">
                  {{t "group_invite.available_count" count=this.formattedRemainingCapacity}}
                </span>
              </div>
            {{/if}}
            <hr class="mt-6 mb-4" />
            <InviteSteps @steps={{@steps}} />
          {{/unless}}
        </div>
      </div>
    {{/if}}
  </template>
}
